import { NgTemplateOutlet } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { TUI_DATE_SEPARATOR, TuiDay } from "@taiga-ui/cdk";
import { TuiButtonModule, TuiDialogService, TuiHostedDropdownModule, TuiLabelModule, TuiLoaderModule } from "@taiga-ui/core";
import { TuiInputDateModule, TuiInputModule, TuiInputNumberModule, tuiInputNumberOptionsProvider } from "@taiga-ui/kit";
import { addMonths, addDays, isValid } from "date-fns";
import { catchError, EMPTY, Subject, takeUntil } from "rxjs";
import { Store } from "@ngrx/store";
import { InventoryActions } from "@estatex/state/inventory";
import { Requirement } from "@estatex/types";
import { InventoryService } from "@estatex/services";
import { environment } from "@estatex/environments";

@Component({
    selector: "app-booking-form",
    standalone: true,
    imports: [
        ReactiveFormsModule,
        FormsModule,
        TuiInputDateModule,
        TuiLabelModule,
        TuiButtonModule,
        TuiHostedDropdownModule,
        TuiInputModule,
        TuiInputNumberModule,
        NgTemplateOutlet,
        TuiLoaderModule,
    ],
    templateUrl: "./booking-form.component.html",
    styleUrl: "./booking-form.component.css",
    providers: [
        { provide: TUI_DATE_SEPARATOR, useValue: "/" },
        tuiInputNumberOptionsProvider({
            decimal: "never",
            step: 1,
        }),
    ],
})
export class BookingFormComponent implements OnInit, OnDestroy {
    private unubscribe$ = new Subject<void>();
    loading: boolean = false;

    requirementText = "";
    open = false;

    minDate = new Date();
    maxDate = addMonths(new Date(), 3);

    minPossibleDate: TuiDay;
    maxPossibleDate: TuiDay;

    minPossibleCheckoutDate: TuiDay;

    bookingForm = this.fb.group({
        checkinDate: this.fb.control<TuiDay>(null, [Validators.required]),
        checkoutDate: this.fb.control<TuiDay>(null, [Validators.required]),
    });

    requirementsForm = this.fb.group({
        rooms: this.fb.control(1, [Validators.required, Validators.max(6), Validators.min(1)]),
        adults: this.fb.control(2, [Validators.required, Validators.max(2), Validators.min(1)]),
        child: this.fb.control(0, [Validators.required, Validators.max(1)]),
    });

    maxAdults = 2;
    minAdults = 1;
    maxChild = 1;

    showBookingText = false;

    modalTitle: string;
    modalContent: string;

    constructor(
        private readonly fb: FormBuilder,
        private readonly router: Router,
        private readonly ngrxStore: Store,
        private readonly tuiDialogService: TuiDialogService,
        private readonly inventoryService: InventoryService
    ) {}

    /**
     * Lifecycle method
     */
    ngOnInit(): void {
        this.requirementText = `${this.requirementsForm.controls.rooms.value} Rooms, ${this.requirementsForm.controls.adults.value} Adults, ${this.requirementsForm.controls.child.value} Child`;

        this.requirementsForm.valueChanges.pipe(takeUntil(this.unubscribe$)).subscribe(() => {
            this.requirementText = `${this.requirementsForm.controls.rooms.value || 0} Rooms, ${
                this.requirementsForm.controls.adults.value || 0
            } Adults, ${this.requirementsForm.controls.child.value || 0} Child`;

            // Need changes here to change validation in reactive form
            this.maxAdults = this.requirementsForm.controls.rooms.value * 2;
            this.minAdults = this.requirementsForm.controls.rooms.value;
            this.maxChild = this.requirementsForm.controls.rooms.value;

            // Setting the minimum value if rooms are increased
            if (this.requirementsForm.controls.adults.value < this.minAdults) {
                this.requirementsForm.controls.adults.setValue(this.minAdults);
            }

            // Setting the maximum value if the maxAdults is less than current value
            if (this.requirementsForm.controls.adults.value > this.maxAdults) {
                this.requirementsForm.controls.adults.setValue(this.maxAdults);
            }

            //Refreshing the validators
            this.requirementsForm.controls.adults.setValidators([
                Validators.required,
                Validators.max(this.maxAdults),
                Validators.min(this.minAdults),
            ]);
            this.requirementsForm.controls.child.setValidators([Validators.required, Validators.max(this.maxChild)]);
            this.requirementsForm.updateValueAndValidity();
        });

        this.minPossibleDate = new TuiDay(this.minDate.getFullYear(), this.minDate.getMonth(), this.minDate.getDate());
        this.maxPossibleDate = new TuiDay(this.maxDate.getFullYear(), this.maxDate.getMonth(), this.maxDate.getDate());
        this.minPossibleCheckoutDate = this.minPossibleDate;

        this.bookingForm.valueChanges.pipe(takeUntil(this.unubscribe$)).subscribe((value) => {
            if (!value?.checkinDate) {
                this.minPossibleCheckoutDate = this.minPossibleDate;
                return;
            }
            const checkinDate = addDays(value.checkinDate.toLocalNativeDate(), 1);
            this.minPossibleCheckoutDate = isValid(checkinDate)
                ? new TuiDay(checkinDate.getFullYear(), checkinDate.getMonth(), checkinDate.getDate())
                : this.minPossibleDate;
        });
    }

    /**
     * Called when submit button is clicked
     * @returns void
     */
    onSubmit(): void {
        // Disabled validation check on the booking form to display feature not available message
        if (!(this.requirementsForm.valid && this.bookingForm.valid)) {
            return;
        }
        this.loading = true;

        const checkinDate = this.bookingForm.controls.checkinDate.value.toLocalNativeDate();
        const checkoutDate = this.bookingForm.controls.checkoutDate.value.toLocalNativeDate();

        // Creating the requirement object
        const requirement: Requirement = {
            checkinDate,
            checkoutDate,
            rooms: this.requirementsForm.controls.rooms.value,
            adults: this.requirementsForm.controls.adults.value,
            child: this.requirementsForm.controls.child.value,
        };

        this.ngrxStore.dispatch(InventoryActions.saveRequirement({ requirement }));

        this.inventoryService
            .getInventoryForDates(checkinDate, checkoutDate, environment.PROPERTY_ID)
            .pipe(
                takeUntil(this.unubscribe$),
                catchError((err) => {
                    // this.modalTitle = "Unexpected Error";
                    // this.modalContent = "Please try again";
                    // this.tuiDialogService
                    //     .open(`<h4 class="text-sm">${this.modalContent}</h4>`, {
                    //         label: this.modalTitle,
                    //         data: { button: "Close" },
                    //         header: this.modalTitle,
                    //         appearance: "booking-form-error",
                    //     })
                    //     .pipe(takeUntil(this.unubscribe$))
                    //     .subscribe();

                    this.modalContent = "Please Call +91 89723 84814 to book your room";
                    this.tuiDialogService
                        // .open(`<h4>${this.modalTitle}</h4>`, {
                        .open(``, {
                            label: this.modalContent,
                            size: "s",
                            data: { button: "Close" },
                        })
                        .pipe(takeUntil(this.unubscribe$))
                        .subscribe();
                    this.loading = false;
                    return EMPTY;
                })
            )
            .subscribe((inventory) => {
                const isInventoryAvailable = this.inventoryService.checkInventoryAgainstRequirement(requirement, inventory);
                if (isInventoryAvailable) {
                    this.router.navigate(["book"]);
                    return;
                }
                // this.modalTitle = "No Rooms available on the entered dates";
                this.modalContent = "Please Call +91 89723 84814 to book your room";
                this.tuiDialogService
                    // .open(`<h4>${this.modalTitle}</h4>`, {
                    .open(``, {
                        label: this.modalContent,
                        size: "s",
                        data: { button: "Close" },
                    })
                    .pipe(takeUntil(this.unubscribe$))
                    .subscribe();
                this.loading = false;
            });
    }

    /**
     * OnDestroy lifecycle method
     */
    ngOnDestroy(): void {
        this.unubscribe$.next();
        this.unubscribe$.complete();
    }
}
